import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { fetchOssOrderDetail, getOssDocumentDownloadUrl } from "../../api/ossApi";
import { actionStatus, displayLabel, sourceType } from "../../constants/labels";
import { OssStatusChip } from "../OssStatusChip";

const fieldSx = {
  "& .MuiInputBase-root": {
    bgcolor: "#fff",
  },
};

const InfoCell = ({ label, value, strong = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 11.8, color: "#64748b", fontWeight: 700 }}>{label}</Typography>
    <Typography noWrap sx={{ mt: 0.25, fontSize: 13.2, color: "#111827", fontWeight: strong ? 900 : 700 }}>
      {value || "-"}
    </Typography>
  </Box>
);

const TicketPanel = ({ title, icon, children, action }) => (
  <Box
    sx={{
      p: 1.45,
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      bgcolor: "#fff",
      minWidth: 0,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.15 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <Box sx={{ display: "grid", placeItems: "center", color: "#6c5ce7", "& svg": { fontSize: 19 } }}>
          {icon}
        </Box>
        <Typography noWrap sx={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
    {children}
  </Box>
);

const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "-";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

const buildTicketNote = ({ mode, contactName, contactMethod, contactResult, note, item }) => {
  const prefix =
    mode === "NOTE"
      ? "[기록 저장]"
      : mode === "FOLLOW_UP"
        ? "[다시 확인 예약]"
        : "[완료 처리]";
  const lines = [
    `${prefix} ${item?.exception_title || "조치 필요"} 확인`,
    contactName ? `통화/확인 대상: ${contactName}` : "",
    contactMethod ? `확인 방식: ${contactMethod}` : "",
    contactResult ? `확인 결과: ${contactResult}` : "",
    note ? `메모: ${note}` : "",
  ].filter(Boolean);
  return lines.join("\n");
};

const getTargetLine = (detail, item) =>
  (detail?.lines || []).find((line) => String(line.order_line_id) === String(item?.order_line_id)) || null;

const modeOptions = [
  {
    value: "DONE",
    label: "완료 처리",
    helper: "확인 결과를 남기고 조치 필요 목록에서 제외합니다.",
  },
  {
    value: "FOLLOW_UP",
    label: "다시 확인 예약",
    helper: "선택한 다음 확인일에 다시 확인 대상으로 남깁니다.",
  },
  {
    value: "NOTE",
    label: "기록만 저장",
    helper: "전화·확인 내용을 이력에 남기고 상태는 유지합니다.",
  },
];

export const OssActionProcessDialog = ({
  open,
  item,
  groupCode,
  projectCode,
  loading,
  onClose,
  onSubmit,
}) => {
  const [note, setNote] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactMethod, setContactMethod] = useState("전화");
  const [contactResult, setContactResult] = useState("");
  const [mode, setMode] = useState("DONE");
  const [nextCheckDate, setNextCheckDate] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    if (!open) return;
    setNote("");
    setContactName("");
    setContactMethod("전화");
    setContactResult("");
    setMode("DONE");
    setNextCheckDate("");
  }, [open, item?.order_line_id]);

  useEffect(() => {
    let alive = true;
    const loadDetail = async () => {
      if (!open || !item?.order_id || !groupCode || !projectCode) return;
      setDetailLoading(true);
      setDetailError("");
      try {
        const data = await fetchOssOrderDetail(groupCode, projectCode, item.order_id);
        if (alive) setDetail(data);
      } catch (error) {
        if (alive) {
          setDetail(null);
          setDetailError(error.response?.data?.message || "주문 상세 정보를 불러오지 못했습니다.");
        }
      } finally {
        if (alive) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      alive = false;
    };
  }, [groupCode, item?.order_id, open, projectCode]);

  if (!item) return null;

  const isFollowUp = mode === "FOLLOW_UP";
  const isNoteOnly = mode === "NOTE";
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowValue = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const submitDisabled = loading || (isFollowUp && (!nextCheckDate || !note.trim())) || (isNoteOnly && !note.trim());
  const targetLine = getTargetLine(detail, item);
  const documents = detail?.documents || [];
  const histories = detail?.histories || [];
  const actions = detail?.actions || [];
  const historyRows = histories.length ? histories : actions;

  const orderInfo = detail?.order || {};
  const targetSummary = targetLine || item;
  const composedNote = buildTicketNote({ mode, contactName, contactMethod, contactResult, note, item });

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: "min(1180px, calc(100vw - 32px))",
          height: "min(760px, calc(100dvh - 32px))",
          maxHeight: "calc(100dvh - 32px)",
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: "#eef2f7",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: "20px !important",
          py: "14px !important",
          pr: "56px !important",
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "#fff",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              display: "grid",
              placeItems: "center",
              borderRadius: "8px",
              bgcolor: "#fff7ed",
              color: "#f59e0b",
              flexShrink: 0,
            }}
          >
            <CallOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 20, fontWeight: "900 !important", color: "#111827", lineHeight: 1.2 }}>
              조치 티켓 처리
            </Typography>
            <Typography noWrap sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
              {item.order_no} · {item.customer_name || "-"} · {item.item_name_snapshot || "-"}
            </Typography>
          </Box>
        </Stack>
        <Button
          color="inherit"
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            minWidth: 34,
            width: 34,
            height: 34,
            p: 0,
            color: "#6b7280",
          }}
        >
          <CloseRoundedIcon />
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          p: "14px !important",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          bgcolor: "#eef2f7",
        }}
      >
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(460px, 0.94fr) minmax(420px, 1.06fr)" },
            gap: 1.25,
          }}
        >
          <Stack spacing={1.15} sx={{ minHeight: 0, overflow: "hidden" }}>
            <TicketPanel title="처리 대상" icon={<FactCheckOutlinedIcon />}>
              <Stack spacing={1}>
                <Box sx={{ p: 1.1, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #e5e7eb" }}>
                  <Typography sx={{ fontSize: 14.2, fontWeight: 900, color: "#111827" }}>
                    {item.exception_title || "확인 필요"}
                  </Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 12.4, color: "#64748b", lineHeight: 1.45 }}>
                    {item.exception_message || "주문 일정 확인 또는 생산계획 검토가 필요합니다."}
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                  <InfoCell label="주문번호" value={item.order_no} strong />
                  <InfoCell label="거래처" value={item.customer_name} strong />
                  <InfoCell label="품목" value={item.item_name_snapshot} strong />
                  <InfoCell label="요청 납기" value={item.requested_due_date} strong />
                  <InfoCell label="다음 확인" value={item.next_check_date || item.check_required_date} />
                  <InfoCell label="납품처/현장" value={item.site_name || orderInfo.site_name} />
                </Box>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  <OssStatusChip value={item.schedule_status} />
                  <OssStatusChip type="risk" value={item.risk_level} />
                </Stack>
              </Stack>
            </TicketPanel>

            <TicketPanel title="조치 입력" icon={<CallOutlinedIcon />}>
              <Stack spacing={1.05}>
                <RadioGroup
                  value={mode}
                  onChange={(event) => setMode(event.target.value)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                    gap: 0.8,
                    "& .MuiFormControlLabel-label": { fontSize: 13.2, color: "#374151" },
                  }}
                >
                  {modeOptions.map((option) => {
                    const selected = mode === option.value;
                    return (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" />}
                        sx={{
                          m: 0,
                          px: 1.15,
                          py: 1,
                          alignItems: "flex-start",
                          border: `1px solid ${selected ? "#6c5ce7" : "#e5e7eb"}`,
                          borderRadius: "8px",
                          bgcolor: selected ? "#f5f3ff" : "#fff",
                        }}
                        label={
                          <Box>
                            <Typography sx={{ fontSize: 13.3, fontWeight: "900 !important", color: "#111827" }}>
                              {option.label}
                            </Typography>
                            <Typography sx={{ mt: 0.25, fontSize: 11.7, color: "#64748b", lineHeight: 1.35 }}>
                              {option.helper}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </RadioGroup>

                {isFollowUp && (
                  <TextField
                    label="다음 확인일"
                    type="date"
                    value={nextCheckDate}
                    onChange={(event) => setNextCheckDate(event.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: tomorrowValue }}
                    helperText="내일 이후 날짜만 선택할 수 있습니다."
                    sx={fieldSx}
                  />
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 0.8fr" }, gap: 1 }}>
                  <TextField
                    label="통화/확인 대상"
                    value={contactName}
                    onChange={(event) => setContactName(event.target.value)}
                    size="small"
                    placeholder="예: 구매팀 김대리"
                    sx={fieldSx}
                  />
                  <TextField
                    label="확인 방식"
                    value={contactMethod}
                    onChange={(event) => setContactMethod(event.target.value)}
                    size="small"
                    placeholder="전화, 메일, 메신저"
                    sx={fieldSx}
                  />
                </Box>
                <TextField
                  label="확인 결과"
                  value={contactResult}
                  onChange={(event) => setContactResult(event.target.value)}
                  size="small"
                  placeholder="예: 납기 유지 가능, 생산계획 확인 필요"
                  sx={fieldSx}
                />
                <TextField
                  label="조치 메모"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  multiline
                  minRows={4}
                  placeholder={
                    isFollowUp
                      ? "현재 확인 내용과 다시 확인해야 하는 이유를 입력"
                      : isNoteOnly
                        ? "통화 내용, 확인 내용 등 이력으로 남길 내용을 입력"
                        : "확인 결과와 처리 내용을 입력"
                  }
                  required={isFollowUp || isNoteOnly}
                  helperText={
                    isFollowUp
                      ? "다시 확인 예약 시 메모는 필수입니다."
                      : isNoteOnly
                        ? "기록만 저장할 때는 메모가 필수입니다."
                        : "메모를 입력하면 조치 이력에 남습니다."
                  }
                  sx={fieldSx}
                />
              </Stack>
            </TicketPanel>
          </Stack>

          <Stack spacing={1.15} sx={{ minHeight: 0, overflow: "hidden" }}>
            <TicketPanel title="주문 요약" icon={<ArticleOutlinedIcon />}>
              {detailLoading ? (
                <Box sx={{ minHeight: 92, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={26} />
                </Box>
              ) : detailError ? (
                <Alert severity="warning">{detailError}</Alert>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1 }}>
                  <InfoCell label="고객 발주번호" value={orderInfo.customer_po_no} />
                  <InfoCell label="주문일자" value={orderInfo.order_date} />
                  <InfoCell label="입력 방식" value={displayLabel(sourceType, orderInfo.source_type)} />
                  <InfoCell label="착수 필요일" value={targetSummary.required_start_date} strong />
                  <InfoCell label="확인 필요일" value={targetSummary.check_required_date} />
                  <InfoCell label="남은 여유일" value={targetSummary.available_margin_days ?? "-"} strong />
                </Box>
              )}
            </TicketPanel>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", xl: "1fr 0.92fr" },
                gap: 1.15,
                overflow: "hidden",
              }}
            >
              <TicketPanel title="조치 이력" icon={<HistoryOutlinedIcon />}>
                <Stack
                  spacing={0.8}
                  sx={{
                    maxHeight: { xs: 220, xl: "calc(100dvh - 430px)" },
                    minHeight: 0,
                    overflow: "auto",
                    pr: 0.3,
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": { borderRadius: 999, backgroundColor: "#cbd5e1" },
                  }}
                >
                  {historyRows.length ? (
                    historyRows.slice(0, 20).map((row, index) => (
                      <Box
                        key={`${row.action_id || row.occurred_at || index}-${index}`}
                        sx={{ p: 1.05, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fbfcff" }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                            {row.action_title || row.title || "이력"}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#64748b", flexShrink: 0 }}>
                            {row.due_at || row.occurred_at || ""}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.35, fontSize: 12.2, color: "#64748b" }}>
                          {displayLabel(actionStatus, row.action_status || row.description)}
                        </Typography>
                        {row.result_note || row.note ? (
                          <Typography sx={{ mt: 0.45, fontSize: 12.5, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
                            {row.result_note || row.note}
                          </Typography>
                        ) : null}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ p: 2, borderRadius: "8px", bgcolor: "#f8fafc", color: "#64748b", textAlign: "center", fontSize: 13 }}>
                      아직 남겨진 조치 이력이 없습니다.
                    </Box>
                  )}
                </Stack>
              </TicketPanel>

              <TicketPanel title="문서/발주서" icon={<DescriptionOutlinedIcon />}>
                <Stack
                  spacing={0.8}
                  sx={{
                    maxHeight: { xs: 220, xl: "calc(100dvh - 430px)" },
                    minHeight: 0,
                    overflow: "auto",
                    pr: 0.3,
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": { borderRadius: 999, backgroundColor: "#cbd5e1" },
                  }}
                >
                  {documents.length ? (
                    documents.map((document) => (
                      <Box
                        key={document.document_id}
                        sx={{
                          p: 1.05,
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          bgcolor: "#fbfcff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                            {document.file_name}
                          </Typography>
                          <Typography noWrap sx={{ mt: 0.3, fontSize: 12, color: "#64748b" }}>
                            {document.document_type || "문서"} · {formatFileSize(document.file_size)} · {document.uploaded_at}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewRoundedIcon />}
                          onClick={() => window.open(getOssDocumentDownloadUrl(groupCode, projectCode, document.document_id), "_blank", "noopener,noreferrer")}
                          sx={{ flexShrink: 0, height: 32 }}
                        >
                          열기
                        </Button>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ p: 2, borderRadius: "8px", bgcolor: "#f8fafc", color: "#64748b", textAlign: "center", fontSize: 13 }}>
                      등록된 문서나 발주서가 없습니다.
                    </Box>
                  )}
                </Stack>
              </TicketPanel>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #e5e7eb", bgcolor: "#fff" }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          닫기
        </Button>
        <Button
          variant={isNoteOnly ? "outlined" : "contained"}
          disabled={submitDisabled}
          onClick={() =>
            onSubmit({
              actionStatus: isFollowUp ? "IN_PROGRESS" : isNoteOnly ? "NOTE" : "DONE",
              resultNote: composedNote || (isFollowUp ? `${item.exception_title} 재확인 예약` : `${item.exception_title} 완료 처리`),
              nextCheckDate: isFollowUp ? nextCheckDate : undefined,
              checkResult: isFollowUp ? "UNDECIDED" : "ON_SCHEDULE",
            })
          }
          sx={{ minWidth: 118 }}
        >
          {isFollowUp ? "다시 확인 예약" : isNoteOnly ? "기록 저장" : "완료 처리"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
